import { ExerciseDto } from '../../../shared/dtos/exercise.dto';
import { FC, memo, MouseEvent, ReactElement } from 'react';
import i18next from 'i18next';

type BaseProps = {
  height?: number;
  cornerRadius?: number;
  backgroundFallback?: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  onError?: (error: unknown) => void;
}

type MediaPlayerProps = BaseProps & {
  exercise?: ExerciseDto;
  src?: string;
  poster?: string;
  lazy?: boolean;
  isPlaying?: boolean;
  onRequestPlay?: () => void;
}

type AudioPlayerProps = {
  src: string;
  height: number;
  cornerRadius: number;
  backgroundSrc: string;
  className?: string;
};

const AudioPlayer: FC<AudioPlayerProps> = ({ src, height, cornerRadius, backgroundSrc, className }: AudioPlayerProps): ReactElement => {
  return (
    <div 
      className={`position-relative w-100 overflow-hidden ${className ?? ''}`}
      style={{ height: `${height}px`, borderRadius: `${cornerRadius}px`, background: '#111' }}
    >
      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
        <img
          src={backgroundSrc}
          alt=""
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: `${cornerRadius}px`,
            filter: 'brightness(0.82)',
          }}
        />
      </div>
      
      <div 
        className="position-absolute start-0 end-0 d-flex justify-content-center px-2"
        style={{ bottom: '10px' }}
      >
        <audio
          controls
          preload="metadata"
          style={{ width: '100%', maxWidth: '320px' }}
        >
          <source src={src} />
        </audio>
      </div>
    </div>
  )
};


type MediaPreviewProps = {
  poster: string;
  backgroundFallback: string;
  height: number;
  cornerRadius: number;
  className?: string;
  onRequestPlay?: () => void;
};

const MediaPreview: FC<MediaPreviewProps> = ({
  poster,
  backgroundFallback,
  height,
  cornerRadius,
  className,
  onRequestPlay
}: MediaPreviewProps): ReactElement => {
  const onClickPlay = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onRequestPlay?.();
  };

  return (
    <div
      className={`position-relative w-100 overflow-hidden ${className ?? ''}`}
      style={{ height: `${height}px`, borderRadius: `${cornerRadius}px`, background: '#ececec' }}
    >
      <img
        src={poster}
        alt=""
        loading="lazy"
        className="w-100 h-100"
        style={{ objectFit: 'cover', borderRadius: `${cornerRadius}px` }}
        onError={(event) => {
          if (event.currentTarget.src.endsWith(backgroundFallback)) {
            return;
          }
          event.currentTarget.src = backgroundFallback;
        }}
      />

      <button
        type="button"
        aria-label={i18next.t('exerciseModal.playPreview')}
        onClick={onClickPlay}
        className="btn position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle p-0 border-0"
        style={{ width: '3rem', height: '3rem', background: 'rgba(0, 0, 0, 0.55)' }}
      >
        <i className="fas fa-play text-white" />
      </button>
    </div>
  );
};

const MediaPlayer: FC<MediaPlayerProps> = ({
  exercise,
  src,
  poster,
  height = 140,
  cornerRadius = 8,
  backgroundFallback = '/video_thumbnail.png',
  className,
  autoPlay = false,
  muted = true,
  loop = true,
  onError,
  lazy = false,
  isPlaying = false,
  onRequestPlay
}: MediaPlayerProps): ReactElement => {
  if (lazy && !isPlaying) {
    return (
      <MediaPreview
        poster={poster ?? exercise?.hThumbnail ?? exercise?.vThumbnail ?? backgroundFallback}
        backgroundFallback={backgroundFallback}
        height={height}
        cornerRadius={cornerRadius}
        className={className}
        onRequestPlay={onRequestPlay}
      />
    );
  }

  if (exercise?.mp3File && !exercise.horizontalUrl && !exercise.verticalUrl) {
    return (
      <AudioPlayer
        src={exercise.mp3File}
        height={height}
        cornerRadius={cornerRadius}
        backgroundSrc={backgroundFallback}
        className={className}
      />
    );
  }
  
  const videoSrc: string | undefined = src ?? exercise?.horizontalUrl;
  const videoPoster: string = poster ?? exercise?.hThumbnail ?? exercise?.vThumbnail ?? backgroundFallback;
  
  return (
    <div
      className={`position-relative w-100 overflow-hidden ${className ?? ''}`}
      style={{ height: `${height}px`, borderRadius: `${cornerRadius}px`, background: '#000' }}
    >
      <video
        className="w-100 h-100"
        style={{ objectFit: 'contain', borderRadius: `${cornerRadius}px` }}
        src={videoSrc}
        poster={videoPoster}
        controls
        loop={loop}
        autoPlay={autoPlay || (lazy && isPlaying)}
        muted={muted}
        preload="metadata"
        onError={(error) => onError?.(error)}
      />
    </div>
  );
};

export default memo(MediaPlayer);
